/* eslint-disable jsx-a11y/no-onchange */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Kanban, Toolbar } from '@dhx/kanban';
import { ActivityIndicator } from 'cv-library-react-web';
// Bypasses postcss-loader via raw-loader so the dhtmlxKanban CSS isn't processed
// through postcss-preset-env, which rejects certain CSS custom-property calc()
// expressions in the vendor file with a lexer error. The CSS is injected once
// into <head> at runtime instead.
// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved
import kanbanCssRaw from '!!raw-loader!@dhx/kanban/dist/kanban.css';

const BUSY_KEYFRAMES = '@keyframes rwkanban-busy-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }';

function injectKanbanStyles() {
    const id = 'dhx-kanban-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `${typeof kanbanCssRaw === 'string' ? kanbanCssRaw : kanbanCssRaw.default}\n${BUSY_KEYFRAMES}`;
    document.head.appendChild(el);
}

// Maps common priority label strings (case-insensitive) to hex colors used for
// the card's top stripe (card.color) and the priority badge (cardShape.priority.values).
// Unknown values fall back to PRIORITY_COLOR_DEFAULT so the card still renders.
const PRIORITY_COLORS = {
    high: '#EF4444',
    highest: '#EF4444',
    critical: '#EF4444',
    urgent: '#EF4444',
    p1: '#EF4444',
    '1': '#EF4444',
    medium: '#F97316',
    normal: '#F97316',
    moderate: '#F97316',
    p2: '#F97316',
    '2': '#F97316',
    low: '#22C55E',
    lowest: '#22C55E',
    minor: '#22C55E',
    trivial: '#22C55E',
    p3: '#22C55E',
    '3': '#22C55E',
};
const PRIORITY_COLOR_DEFAULT = '#94A3B8';

// Static list of all priority values for cardShape.priority.values.
// dhtmlxKanban matches card.priority (normalized string id) against this list
// to render the colored priority badge. Must live in cardShape, NOT at top level.
const ALL_PRIORITY_VALUES = [
    { id: 'high', color: '#EF4444', label: 'High' },
    { id: 'highest', color: '#EF4444', label: 'Highest' },
    { id: 'critical', color: '#EF4444', label: 'Critical' },
    { id: 'urgent', color: '#EF4444', label: 'Urgent' },
    { id: 'p1', color: '#EF4444', label: 'P1' },
    { id: '1', color: '#EF4444', label: '1' },
    { id: 'medium', color: '#F97316', label: 'Medium' },
    { id: 'normal', color: '#F97316', label: 'Normal' },
    { id: 'moderate', color: '#F97316', label: 'Moderate' },
    { id: 'p2', color: '#F97316', label: 'P2' },
    { id: '2', color: '#F97316', label: '2' },
    { id: 'low', color: '#22C55E', label: 'Low' },
    { id: 'lowest', color: '#22C55E', label: 'Lowest' },
    { id: 'minor', color: '#22C55E', label: 'Minor' },
    { id: 'trivial', color: '#22C55E', label: 'Trivial' },
    { id: 'p3', color: '#22C55E', label: 'P3' },
    { id: '3', color: '#22C55E', label: '3' },
];

function priorityId(rawValue) {
    if (rawValue == null) {
        return null;
    }
    return String(rawValue)
        .toLowerCase()
        .trim();
}

function priorityColor(id) {
    return id ? (PRIORITY_COLORS[id] || PRIORITY_COLOR_DEFAULT) : null;
}

// Coarse 0(highest)-2(lowest) tier derived from the same red/orange/green grouping
// PRIORITY_COLORS already uses, so "sort by priority" has a sensible order even though
// priority values are project-specific strings with no universal total ordering (see
// buildPriorityOptions in Kanban.js) - unrecognized values sort last.
const PRIORITY_RANK = { '#EF4444': 0, '#F97316': 1, '#22C55E': 2 };
function priorityRank(id) {
    const color = priorityColor(id);
    return color in PRIORITY_RANK ? PRIORITY_RANK[color] : 3;
}

// Options for DHTMLX's own native Toolbar "sort" item (the icon + dropdown menu at
// https://dhtmlx.com/docs/products/dhtmlxKanban/ - "Label (a-z)/(z-a)", "Priority (lowest/
// highest first)", etc.) - mirrors that same menu shape/wording but over this board's own
// sortable fields (title/due date/priority) instead of the demo's label/description.
const SORT_OPTIONS = [
    {
        id: 'title-asc', text: 'Title (A-Z)', icon: 'wxi-asc', by: 'label', dir: 'asc', preserve: true,
    },
    {
        id: 'title-desc', text: 'Title (Z-A)', icon: 'wxi-desc', by: 'label', dir: 'desc', preserve: true,
    },
    {
        id: 'duedate-asc',
        text: 'Due date (soonest)',
        icon: 'wxi-asc',
        by: (card) => (card.end_date ? new Date(card.end_date).getTime() : Infinity),
        dir: 'asc',
        preserve: true,
    },
    {
        id: 'duedate-desc',
        text: 'Due date (latest)',
        icon: 'wxi-desc',
        by: (card) => (card.end_date ? new Date(card.end_date).getTime() : -Infinity),
        dir: 'desc',
        preserve: true,
    },
    {
        id: 'priority-desc', text: 'Priority (High to Low)', icon: 'wxi-desc', by: (card) => priorityRank(card.priority), dir: 'asc', preserve: true,
    },
    {
        id: 'priority-asc', text: 'Priority (Low to High)', icon: 'wxi-asc', by: (card) => priorityRank(card.priority), dir: 'desc', preserve: true,
    },
];

// Plain inline styles for the board's own filter toolbar - deliberately minimal (no theme/
// design-system dependency) since this toolbar is local to the Kanban widget, not part of
// the app's own chrome. Search and sort themselves use DHTMLX's own native Toolbar widget
// (mounted separately, see toolbarContainerRef) instead of plain HTML controls.
const TOOLBAR_STYLE = {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
    padding: '8px 12px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC',
};
const SELECT_STYLE = {
    padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13, background: '#fff',
};
const LOADING_OVERLAY_STYLE = {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 12, background: 'rgba(248, 250, 252, 0.75)', color: '#334155', fontSize: 14,
};
const BUSY_PILL_STYLE = {
    position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 30,
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 16,
    background: '#1E293B', color: '#fff', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    pointerEvents: 'none',
};
const BUSY_BAR_STYLE = {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 30, overflow: 'hidden',
    background: 'rgba(59, 130, 246, 0.2)', pointerEvents: 'none',
};
const BUSY_BAR_FILL_STYLE = {
    height: '100%', width: '35%', background: '#3B82F6', animation: 'rwkanban-busy-slide 1.1s ease-in-out infinite',
};
const TOAST_STYLE = {
    position: 'fixed', bottom: 56, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
    padding: '8px 16px', borderRadius: 6, color: '#fff', fontSize: 13,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)', pointerEvents: 'none',
};
const TOAST_COLORS = { success: '#16A34A', error: '#DC2626' };
const CLEAR_BUTTON_STYLE = {
    padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13,
    background: '#fff', cursor: 'pointer',
};

function normalizeRows(rows) {
    return (rows || []).map((r) => (typeof r === 'object' ? r : { id: String(r), label: String(r) }));
}

// DHTMLX Kanban has no built-in per-card filter predicate (its own setSearch() only
// highlights/dims matches, it doesn't hide the rest - that's why search is handled by
// DHTMLX's own Toolbar widget below instead) - so narrowing which cards are visible for
// priority/assignee is done here, client-side, on the already-built dhx card list, before
// it's ever handed to the widget.
function filterDhxCards(cards, { priorityFilter, assigneeFilter }) {
    return (cards || []).filter((card) => {
        if (priorityFilter && priorityFilter !== 'all' && card.priority !== priorityFilter) {
            return false;
        }
        if (assigneeFilter && assigneeFilter !== 'all') {
            if (!Array.isArray(card.users) || !card.users.includes(assigneeFilter)) return false;
        }
        return true;
    });
}

// Converts Kanban.js's plain priority label list (derived from what's actually valid
// for this project - see buildPriorityOptions there) into the {id, color, label} shape
// this editor field and the card badge both need. Falls back to ALL_PRIORITY_VALUES'
// labels only if no options were supplied at all (defensive; Kanban.js always sends a
// non-empty list).
function buildPriorityDropdownValues(priorityOptions) {
    const labels = (priorityOptions && priorityOptions.length > 0)
        ? priorityOptions
        : ALL_PRIORITY_VALUES.map(({ label }) => label);
    return labels.map((label) => {
        const id = priorityId(label);
        return { id, color: priorityColor(id), label: String(label) };
    });
}

// Restricted set of fields shown in DHTMLX's own "add card" modal editor (distinct from
// cardShape, which only governs the rendered card face) - Jira-compatible fields only.
// Status is deliberately never included: Jira issues always start in the workflow's
// default status and reject a status value on create, regardless of which column's "+"
// was clicked - see the add-card interception logic in the component below.
function buildEditorShape(rows, assigneeOptions, priorityOptions, issueTypeOptions, editorFields, parentIssueOptions) {
    const shape = [ { type: 'text', key: 'label', label: 'Title' } ];
    if (issueTypeOptions && issueTypeOptions.length > 0) {
        // Create-only (Jira can't change an existing issue's type via a field update), so
        // edits to it on an existing card are ignored by Kanban.js's handleOnCardEdit.
        shape.push({
            type: 'select',
            key: 'issuetype',
            label: 'Issue type (new cards only)',
            values: issueTypeOptions.map((label) => { return { id: String(label), label: String(label) }; }),
        });
    }
    shape.push(
        { type: 'textarea', key: 'description', label: 'Description' },
        // values are this project's own currently-valid priorities (see
        // buildPriorityOptions in Kanban.js) - not a hardcoded guess at Jira's scheme,
        // which varies per project and previously offered choices Jira rejected on
        // create ("The priority selected is invalid.").
        { type: 'select', key: 'priority', label: 'Priority', values: buildPriorityDropdownValues(priorityOptions) },
        { type: 'date', key: 'end_date', label: 'Due date' },
    );
    const swimlaneOptions = normalizeRows(rows);
    if (swimlaneOptions.length > 0) {
        shape.push({ type: 'select', key: 'row', label: 'Swimlane', values: swimlaneOptions, config: { clear: true } });
    }
    if (parentIssueOptions && parentIssueOptions.length > 0) {
        // Create-only, like the issue type: the parent a new Sub-task is created under (the Story/Task
        // chosen here). Ignored on an existing card; Kanban.js's handleOnCardAdd reads it.
        shape.push({
            type: 'select',
            key: 'parentIssue',
            label: 'Parent issue (Sub-task, new cards only)',
            values: parentIssueOptions,
            config: { clear: true },
        });
    }
    if (assigneeOptions && assigneeOptions.length > 0) {
        // DHTMLX's editorShape has no single-value "user" field type, only "multiselect"
        // (matching card.users' array shape) - a v1 simplification that lets the form UI
        // pick more than one assignee even though only the first selected id is actually
        // used (see Kanban.js's handleOnCardAdd).
        shape.push({ type: 'multiselect', key: 'users', label: 'Assignee', values: assigneeOptions });
    }
    // Only offer fields the board actually maps to a data point (all of them if unspecified).
    return editorFields ? shape.filter(({ key }) => editorFields.includes(key)) : shape;
}

// Builds the cardShape config with the current unique-users list.
// Called at init and on each data refresh so cardShape.users.values stays in
// sync with the assignees actually present on the board.
function buildCardShape(users) {
    return {
        label: { show: true },
        description: { show: true },
        progress: { show: false },
        users: {
            show: true,
            // IUser[] { id, label } — dhtmlx matches card.users (ID array) against this list
            values: users || [],
        },
        priority: {
            show: true,
            // Priority badge values; card.priority must equal one of these ids
            values: ALL_PRIORITY_VALUES,
        },
        end_date: { show: true },
        color: { show: true },
        cover: { show: false },
        menu: { show: false },
        // headerFields renders arbitrary card properties in the card header area
        headerFields: [ { key: 'labelsDisplay', css: 'dhx-card-labels' } ],
    };
}

// dhtmlxKanban reads a card's id back from a DOM attribute and turns any numeric string into
// a Number, then compares it strictly with the card's own id - so a purely numeric id (e.g. a
// SQL serial key) has to be a Number up front, or clicking the card opens an empty editor.
// Ids that aren't a canonical number (Jira keys like "SCRUM-7", "007") are left untouched.
function toDhxCardId(id) {
    if (typeof id === 'string' && id !== '' && String(id * 1) === id) {
        return id * 1;
    }
    return id;
}

/**
 * Converts the Salt column/card model into dhtmlxKanban's data shape.
 * Returns { columns, cards, rows, users } where users is the deduplicated
 * assignee list needed by cardShape.users.values.
 */
function toDhxData(columns, rows) {
    const dhxColumns = [];
    const dhxCards = [];
    const usersMap = new Map(); // assignee string → IUser { id, label }

    (columns || []).forEach(({ status, cards }) => {
        dhxColumns.push({ id: status, label: status });

        (cards || []).forEach(({ id, title, description, priority, progress, assignee, assigneeId, dueDate, swimlane, labels, issueType }) => {
            const pid = priorityId(priority);
            const pColor = priorityColor(pid);

            // Keyed by accountId (falling back to the display name only if a record
            // somehow lacks it) so this id space matches the edit form's assignee picker
            // (see Kanban.js's buildAssigneeOptions) - both must agree on what a "user id"
            // is, or the picker can't tell which option is the card's current assignee.
            const hasAssigneeId = assigneeId != null && assigneeId !== '';
            const hasAssigneeName = assignee != null && assignee !== '';
            let userId = null;
            if (hasAssigneeId) {
                userId = String(assigneeId);
            }
            else if (hasAssigneeName) {
                userId = String(assignee);
            }
            if (userId != null && !usersMap.has(userId)) {
                usersMap.set(userId, { id: userId, label: hasAssigneeName ? String(assignee) : userId });
            }

            const card = {
                id: toDhxCardId(id),
                label: title || String(id),
                description: description || '',
                column: status,
            };

            if (pid) {
                card.priority = pid;
                card.color = pColor;
            }
            if (progress != null && !Number.isNaN(Number(progress))) {
                card.progress = Math.round(Number(progress));
            }
            if (userId != null) {
                // ICard.users expects TID[] (IDs only); display objects live in cardShape.users.values
                card.users = [ userId ];
            }
            if (dueDate != null && dueDate !== '') {
                const d = new Date(String(dueDate));
                card.end_date = Number.isNaN(d.getTime()) ? String(dueDate) : d;
            }
            if (swimlane != null && swimlane !== '') {
                card.row = String(swimlane);
            }
            if (issueType != null && issueType !== '') {
                card.issuetype = String(issueType);
            }
            if (Array.isArray(labels) && labels.length > 0) {
                card.labelsDisplay = labels.join(' · ');
            }

            dhxCards.push(card);
        });
    });

    const dhxRows = normalizeRows(rows);
    const dhxUsers = [ ...usersMap.values() ];

    return {
        columns: dhxColumns,
        cards: dhxCards,
        rows: dhxRows,
        users: dhxUsers,
    };
}

export default function RWKanbanBoard({ columns, rows, canDragCards, canCreateCards, assigneeOptions, priorityOptions, priorityChoices, issueTypeOptions, parentIssueOptions, editorFields, canEditCards, onCardPress, onCardDrop, onCardAdd, onCardEdit, isLoading, style }) {
    const containerRef = useRef(null);

    // Transient request feedback: busyLabel while a create/edit/move save is in flight,
    // toast for the outcome (auto-dismissed). isLoading (prop) covers the initial/refresh query.
    const [
        busyLabel,
        setBusyLabel,
    ] = useState(null);
    const [
        toast,
        setToast,
    ] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = (type, text) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, text });
        toastTimerRef.current = setTimeout(() => setToast(null), 3000);
    };
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);
    const kanbanRef = useRef(null);
    const toolbarContainerRef = useRef(null);
    const toolbarRef = useRef(null);

    // Board-level filter - purely client-side view state (see filterDhxCards below), not
    // persisted anywhere or sent back to the server. Search and sort are both handled by
    // DHTMLX's own native Toolbar widget (mounted below), not by React state, since priority/
    // assignee have no native DHTMLX equivalent but search/sort do.
    const [
        priorityFilter,
        setPriorityFilter,
    ] = useState('all');
    const [
        assigneeFilter,
        setAssigneeFilter,
    ] = useState('all');

    // Stable ref so event handlers always call the latest callbacks without re-subscribing.
    const callbacksRef = useRef({ onCardPress, onCardDrop, onCardAdd, onCardEdit });
    callbacksRef.current = { onCardPress, onCardDrop, onCardAdd, onCardEdit };

    // Always-current edit permission for the select-card handler (closure would be stale).
    const canEditCardsRef = useRef(canEditCards);
    canEditCardsRef.current = canEditCards;

    // id→card lookup; dhtmlx events surface only the card id, not the full object.
    const cardMapRef = useRef({});

    // Always-current columns/rows props; used inside event handlers (closure would be stale).
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const assigneeOptionsRef = useRef(assigneeOptions);
    assigneeOptionsRef.current = assigneeOptions;
    // The editor's own priority list; the filter keeps using priorityOptions. Falls back to it
    // when the parent supplies none.
    const editorPriorityOptions = (priorityChoices && priorityChoices.length > 0) ? priorityChoices : priorityOptions;
    const editorPriorityOptionsRef = useRef(editorPriorityOptions);
    editorPriorityOptionsRef.current = editorPriorityOptions;
    const issueTypeOptionsRef = useRef(issueTypeOptions);
    issueTypeOptionsRef.current = issueTypeOptions;
    const editorFieldsRef = useRef(editorFields);
    editorFieldsRef.current = editorFields;
    const parentIssueOptionsRef = useRef(parentIssueOptions);
    parentIssueOptionsRef.current = parentIssueOptions;

    // When true, a status-update save is in flight — suppress widget.parse so the
    // optimistic card position is preserved during the API round-trip.
    const savingRef = useRef(false);

    // Snapshot of columns before the drag so we can roll back on save failure.
    const preDropColumnsRef = useRef(columns);

    // The column a user actually clicked "+" on, captured by the intercept below before
    // it's possibly force-corrected to the default/initial-status column - compared again
    // in the add-card handler to know whether a redirect happened.
    const lastRequestedColumnRef = useRef(null);

    // ids of cards added via "+" that dhtmlxKanban is holding locally but that haven't
    // been created on the backend yet - the actual create only happens once the user
    // finishes filling in the editor panel (see the set-edit handler below), not on
    // add-card itself, so the panel is what lets them enter a real title/fields first.
    // A card id NOT in this set that's being edited is an existing, already-saved record -
    // see finalizeEditedCard below.
    const pendingNewCardIdsRef = useRef(new Set());
    // { [cardId]: { wasRedirected, defaultColumnId } } captured at add-card time, needed
    // again once that card's editor closes and the real create is finally performed.
    const pendingCardInfoRef = useRef({});
    // The card id the editor panel is currently open for (or null) - comparing this on
    // each set-edit event is how we detect the panel closing/switching away from a card
    // (pending-new or pre-existing), which is the actual "user is done" signal.
    const currentEditCardIdRef = useRef(null);
    // Full columns snapshot taken right before a pre-existing card's editor opens, so a
    // failed update can restore that card (and everything else) exactly as it was -
    // mirrors preDropColumnsRef's role for move-card's own rollback.
    const preEditColumnsSnapshotRef = useRef(null);

    // Keep card lookup map in sync, but not while an optimistic save is in flight.
    useEffect(() => {
        if (savingRef.current) return;
        const map = {};
        (columns || []).forEach(({ cards }) => {
            (cards || []).forEach((card) => { map[String(card.id)] = card; });
        });
        cardMapRef.current = map;
    }, [ columns ]);

    // Init once on mount; destroyed on unmount.
    useEffect(() => {
        injectKanbanStyles();
        const data = toDhxData(columnsRef.current, rowsRef.current);
        const visibleCards = filterDhxCards(data.cards, { priorityFilter, assigneeFilter });

        const widget = new Kanban(containerRef.current, {
            columns: data.columns,
            cards: visibleCards,
            rows: data.rows,
            // columnKey defaults to "column" (matching card.column below, set with no extra
            // config), but rowKey's own default is "" - an empty string, which disables
            // swimlane grouping entirely regardless of a non-empty rows array or card.row
            // values. Without this, the board silently never showed swimlanes at all.
            rowKey: 'row',
            // edit must be on for the editor panel's fields to be typable - for a pending new
            // card as well as a pre-existing one opened by clicking it (see the select-card
            // handler below); saving either kind is handled by the set-edit "on" handler.
            readonly: { add: canCreateCards, edit: canEditCards || canCreateCards, dnd: canDragCards },
            theme: { name: 'material' },
            cardShape: buildCardShape(data.users),
            editorShape: buildEditorShape(rowsRef.current, assigneeOptionsRef.current, editorPriorityOptionsRef.current, issueTypeOptionsRef.current, editorFieldsRef.current, parentIssueOptionsRef.current),
        });

        // Jira (and most workflow-driven systems) can't create an issue directly "into" an
        // arbitrary status - a new issue always starts in the workflow's default status. So
        // "+" is left enabled on every column (simplest, matches DHTMLX's native behavior),
        // but a click on any column other than the first/default one is silently redirected
        // here - before the card is even created - to the default column instead. The
        // add-card handler below still runs normally either way; it's told whether a
        // redirect happened so it can surface an explanatory message.
        widget.api.intercept('add-card', (config) => {
            lastRequestedColumnRef.current = config.columnId;
            const cols = columnsRef.current || [];
            const defaultColumnId = cols.length > 0 ? cols[0].status : null;
            if (defaultColumnId != null && config.columnId !== defaultColumnId) {
                return { ...config, columnId: defaultColumnId };
            }
            return config;
        });

        const finalizePendingCard = (id) => {
            pendingNewCardIdsRef.current.delete(id);
            const info = pendingCardInfoRef.current[id] || {};
            delete pendingCardInfoRef.current[id];

            const card = widget.api.getCard(id);
            if (!card) {
                savingRef.current = pendingNewCardIdsRef.current.size > 0;
                return;
            }

            savingRef.current = true;
            setBusyLabel('Creating card…');
            Promise.resolve(callbacksRef.current.onCardAdd(card, info))
                .then(() => {
                    savingRef.current = pendingNewCardIdsRef.current.size > 0;
                    setBusyLabel(null);
                    showToastRef.current('success', 'Card created');
                })
                .catch(() => {
                    savingRef.current = pendingNewCardIdsRef.current.size > 0;
                    setBusyLabel(null);
                    showToastRef.current('error', 'Could not create card');
                    widget.api.exec('delete-card', { id });
                });
        };

        // A pre-existing card's editor panel just closed - persist whatever the user
        // changed as an update against the real record (unlike finalizePendingCard, there's
        // nothing to create - the record already exists). On failure, unlike the pending-card
        // case (which deletes the not-yet-real card), roll every column back to its pre-edit
        // snapshot - the same rollback approach move-card's own failure handler already uses -
        // since the edited card is real and shouldn't just vanish.
        const finalizeEditedCard = (id) => {
            const preEditColumns = preEditColumnsSnapshotRef.current;
            preEditColumnsSnapshotRef.current = null;

            const card = widget.api.getCard(id);
            if (!card) {
                savingRef.current = false;
                return;
            }

            savingRef.current = true;
            setBusyLabel('Saving changes…');
            Promise.resolve(callbacksRef.current.onCardEdit(card))
                .then(() => {
                    savingRef.current = false;
                    setBusyLabel(null);
                    showToastRef.current('success', 'Card updated');
                })
                .catch(() => {
                    savingRef.current = false;
                    setBusyLabel(null);
                    showToastRef.current('error', 'Could not save changes - reverted');
                    if (preEditColumns) {
                        const rollback = toDhxData(preEditColumns, rowsRef.current);
                        widget.setConfig({ cardShape: buildCardShape(rollback.users) });
                        widget.parse(rollback);
                    }
                });
        };

        widget.api.on('add-card', ({ id }) => {
            const cols = columnsRef.current || [];
            const defaultColumnId = cols.length > 0 ? cols[0].status : null;
            const requestedColumnId = lastRequestedColumnRef.current;
            const wasRedirected = requestedColumnId != null && defaultColumnId != null
                && requestedColumnId !== defaultColumnId;

            pendingNewCardIdsRef.current.add(id);
            pendingCardInfoRef.current[id] = { wasRedirected, defaultColumnId, requestedColumnId };
            savingRef.current = true;

            // Open the editor panel so the user can actually enter a title/description/
            // priority/due date/swimlane/assignee - the real create only happens once
            // they finish, in the set-edit handler below.
            widget.api.exec('set-edit', { cardId: id });
        });

        // An existing card must keep a title. If the panel is closed (or switched to another card)
        // while the title is empty, say so and keep the panel open so it can be fixed, instead of
        // closing with nothing saved and no explanation. A new card is not checked here: its create
        // reports a missing title itself.
        widget.api.intercept('set-edit', (config) => {
            const newEditId = config ? config.cardId : null;
            const previousEditId = currentEditCardIdRef.current;
            if (previousEditId == null || previousEditId === newEditId
                || pendingNewCardIdsRef.current.has(previousEditId) || !canEditCardsRef.current) {
                return config;
            }
            const editedCard = widget.api.getCard(previousEditId);
            if (editedCard && (editedCard.label == null || String(editedCard.label).trim() === '')) {
                showToastRef.current('error', 'Title is required - enter a title before closing');
                return false;
            }
            return config;
        });

        widget.api.on('set-edit', (config) => {
            const newEditId = config ? config.cardId : null;
            const previousEditId = currentEditCardIdRef.current;
            currentEditCardIdRef.current = newEditId;

            if (previousEditId == null || previousEditId === newEditId) {
                return;
            }
            if (pendingNewCardIdsRef.current.has(previousEditId)) {
                finalizePendingCard(previousEditId);
            }
            else if (canEditCardsRef.current) {
                finalizeEditedCard(previousEditId);
            }
        });

        widget.api.on('move-card', ({ id, columnId, rowId }) => {
            preDropColumnsRef.current = columnsRef.current;
            savingRef.current = true;
            setBusyLabel('Moving card…');

            // rowId is the swimlane row the card landed in (absent when the board has no rows).
            Promise.resolve(callbacksRef.current.onCardDrop(String(id), String(columnId), rowId == null ? null : String(rowId)))
                .then(() => {
                    savingRef.current = false;
                    setBusyLabel(null);
                    showToastRef.current('success', 'Card moved');
                })
                .catch(() => {
                    savingRef.current = false;
                    setBusyLabel(null);
                    showToastRef.current('error', 'Could not move card - reverted');
                    const w = kanbanRef.current;
                    if (w) {
                        const rollback = toDhxData(preDropColumnsRef.current, rowsRef.current);
                        w.setConfig({ cardShape: buildCardShape(rollback.users) });
                        w.parse(rollback);
                    }
                });
        });

        widget.api.on('select-card', ({ id }) => {
            const card = cardMapRef.current[String(id)];
            // A pending new card manages its own edit lifecycle from add-card onward
            // (see above) - a click here can only mean a pre-existing card.
            if (pendingNewCardIdsRef.current.has(id)) return;
            if (!canEditCardsRef.current) {
                if (card) callbacksRef.current.onCardPress(card);
                return;
            }
            if (!card) return;
            // Open the same editor panel "+" uses, pre-populated with this card's current
            // values, instead of navigating to the read-only detail view.
            preEditColumnsSnapshotRef.current = columnsRef.current;
            widget.api.exec('set-edit', { cardId: id });
        });

        kanbanRef.current = widget;

        // DHTMLX's own native Toolbar widget instead of plain HTML controls - wired to this
        // Kanban instance via its api, exactly like the demo at
        // https://dhtmlx.com/docs/products/dhtmlxKanban/:
        // - "search": an icon that expands into an input plus a "Search in: Everywhere/
        //   Label/Description" scope picker (dims non-matches, doesn't hide them).
        // - "sort": an icon that opens a menu of sort options (see SORT_OPTIONS above).
        toolbarRef.current = new Toolbar(toolbarContainerRef.current, {
            api: widget.api,
            items: [
                { type: 'search' },
                { type: 'sort', options: SORT_OPTIONS },
            ],
        });

        // The editor panel doesn't apply a textarea's value to the card until another field
        // changes, and its close button tears the panel down first - so text typed last in the
        // Description (the editor's only textarea) was silently dropped. Pushing the current
        // value into the card on pointer-down, before the panel can close, keeps it.
        const commitFocusedTextarea = () => {
            const focused = document.activeElement;
            const editingId = currentEditCardIdRef.current;
            if (editingId != null && focused && focused.tagName === 'TEXTAREA') {
                widget.api.exec('update-card', { id: editingId, card: { description: focused.value } });
            }
        };
        document.addEventListener('pointerdown', commitFocusedTextarea, true);

        return () => {
            document.removeEventListener('pointerdown', commitFocusedTextarea, true);
            if (toolbarRef.current) toolbarRef.current.destructor();
            widget.destructor();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally init-once

    // Sync board data on column/card/row changes — skip while an optimistic save is in flight.
    // setConfig refreshes cardShape.users.values with any newly seen assignees before parse,
    // and editorShape with the current swimlane/assignee option lists.
    useEffect(() => {
        if (savingRef.current) return;
        const widget = kanbanRef.current;
        if (!widget) return;
        const data = toDhxData(columns, rows);
        const visibleCards = filterDhxCards(data.cards, { priorityFilter, assigneeFilter });
        widget.setConfig({
            cardShape: buildCardShape(data.users),
            editorShape: buildEditorShape(rows, assigneeOptions, editorPriorityOptions, issueTypeOptions, editorFields, parentIssueOptions),
        });
        widget.parse({ ...data, cards: visibleCards });
    }, [
        columns,
        rows,
        assigneeOptions,
        editorPriorityOptions,
        issueTypeOptions,
        parentIssueOptions,
        editorFields,
        priorityFilter,
        assigneeFilter,
    ]);

    // Reflect drag/add-permission changes live without remounting.
    useEffect(() => {
        const widget = kanbanRef.current;
        if (!widget) return;
        widget.setConfig({ readonly: { add: canCreateCards, edit: canEditCards || canCreateCards, dnd: canDragCards } });
    }, [
        canDragCards,
        canCreateCards,
        canEditCards,
    ]);

    const priorityFilterOptions = useMemo(
        () => (priorityOptions || []).map((label) => { return { id: priorityId(label), label }; }),
        [ priorityOptions ],
    );

    const hasActiveViewState = priorityFilter !== 'all' || assigneeFilter !== 'all';
    const clearViewState = () => {
        setPriorityFilter('all');
        setAssigneeFilter('all');
        if (kanbanRef.current) {
            kanbanRef.current.setSearch({ value: null });
            kanbanRef.current.setSort(null);
        }
    };

    return (
        <div style={ { display: 'flex', flexDirection: 'column', height: '100%', width: '100%', flexGrow: 1, position: 'relative', ...style } }>
            { busyLabel && (
                <div style={ BUSY_BAR_STYLE }>
                    <div style={ BUSY_BAR_FILL_STYLE } />
                </div>
            ) }
            { busyLabel && (
                <div style={ BUSY_PILL_STYLE }>
                    <ActivityIndicator size={ 14 } />
                    { busyLabel }
                </div>
            ) }
            { isLoading && !busyLabel && (
                <div style={ LOADING_OVERLAY_STYLE }>
                    <ActivityIndicator size={ 36 } />
                    Loading board…
                </div>
            ) }
            { toast && (
                <div style={ { ...TOAST_STYLE, background: TOAST_COLORS[toast.type] } }>
                    { toast.text }
                </div>
            ) }
            <div style={ TOOLBAR_STYLE }>
                <div
                    ref={ toolbarContainerRef }
                    style={ { flex: '1 1 400px', minWidth: 240 } } />
                { priorityFilterOptions.length > 0 && (
                    <select
                        value={ priorityFilter }
                        onChange={ (e) => setPriorityFilter(e.target.value) }
                        style={ SELECT_STYLE }>
                        <option value="all">All priorities</option>
                        { priorityFilterOptions.map(({ id, label }) => (
                            <option
                                key={ id }
                                value={ id }>
                                { label }
                            </option>
                        )) }
                    </select>
                ) }
                { assigneeOptions.length > 0 && (
                    <select
                        value={ assigneeFilter }
                        onChange={ (e) => setAssigneeFilter(e.target.value) }
                        style={ SELECT_STYLE }>
                        <option value="all">All assignees</option>
                        { assigneeOptions.map(({ id, label }) => (
                            <option
                                key={ id }
                                value={ id }>
                                { label }
                            </option>
                        )) }
                    </select>
                ) }
                { hasActiveViewState && (
                    <button
                        type="button"
                        onClick={ clearViewState }
                        style={ CLEAR_BUTTON_STYLE }>
Clear
                    </button>
                ) }
            </div>
            <div
                ref={ containerRef }
                style={ { flexGrow: 1, minHeight: 0 } } />
        </div>
    );
}

RWKanbanBoard.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        status: PropTypes.string,
        cards: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            title: PropTypes.string,
            description: PropTypes.string,
            priority: PropTypes.string,
            progress: PropTypes.number,
            assignee: PropTypes.string,
            assigneeId: PropTypes.string,
            dueDate: PropTypes.string,
            swimlane: PropTypes.string,
            labels: PropTypes.arrayOf(PropTypes.string),
        })),
    })),
    rows: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
    ])),
    canDragCards: PropTypes.bool,
    canCreateCards: PropTypes.bool,
    canEditCards: PropTypes.bool,
    editorFields: PropTypes.arrayOf(PropTypes.string),
    assigneeOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
    })),
    priorityOptions: PropTypes.arrayOf(PropTypes.string),
    priorityChoices: PropTypes.arrayOf(PropTypes.string),
    issueTypeOptions: PropTypes.arrayOf(PropTypes.string),
    parentIssueOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
    })),
    onCardDrop: PropTypes.func,
    onCardAdd: PropTypes.func,
    onCardPress: PropTypes.func,
    onCardEdit: PropTypes.func,
    isLoading: PropTypes.bool,
    style: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]),
};

RWKanbanBoard.defaultProps = {
    columns: [],
    rows: [],
    canDragCards: false,
    canCreateCards: false,
    canEditCards: false,
    editorFields: undefined,
    assigneeOptions: [],
    priorityOptions: [],
    priorityChoices: [],
    issueTypeOptions: [],
    parentIssueOptions: [],
    onCardDrop: () => {},
    onCardAdd: () => Promise.resolve(),
    onCardPress: () => {},
    onCardEdit: () => Promise.resolve(),
    isLoading: false,
    style: null,
};
